<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ContactTeamsAPI from 'dashboard/api/contactTeams';

const props = defineProps({
  contactId: {
    type: [Number, String],
    required: true,
  },
  initialTeamIds: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['updated']);

const { t } = useI18n();
const store = useStore();

const selectedTeamIds = ref([...props.initialTeamIds]);
const isSaving = ref(false);

const teams = computed(() => store.getters['teams/getTeams'] || []);

const teamOptions = computed(() =>
  teams.value.map(team => ({
    value: team.id,
    label: team.name,
  }))
);

const syncFromProps = () => {
  selectedTeamIds.value = [...props.initialTeamIds];
};

watch(
  () => props.initialTeamIds,
  () => syncFromProps(),
  { deep: true }
);

onMounted(() => {
  store.dispatch('teams/get');
  syncFromProps();
});

const saveTeams = async () => {
  isSaving.value = true;
  try {
    const { data } = await ContactTeamsAPI.update({
      contactId: props.contactId,
      teamIds: selectedTeamIds.value,
    });
    emit('updated', data.teams || []);
    useAlert(t('CONTACTS_LAYOUT.PREVIEW.TEAMS.SUCCESS'));
  } catch {
    useAlert(t('CONTACTS_LAYOUT.PREVIEW.TEAMS.ERROR'));
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <section class="space-y-3">
    <h4 class="text-sm font-medium text-n-slate-12">
      {{ t('CONTACTS_LAYOUT.PREVIEW.TEAMS.TITLE') }}
    </h4>
    <p class="text-sm text-n-slate-10">
      {{ t('CONTACTS_LAYOUT.PREVIEW.TEAMS.DESCRIPTION') }}
    </p>
    <TagMultiSelectComboBox
      v-model="selectedTeamIds"
      :options="teamOptions"
      :placeholder="t('CONTACTS_LAYOUT.PREVIEW.TEAMS.PLACEHOLDER')"
    />
    <div class="flex justify-end">
      <Button
        :label="t('CONTACTS_LAYOUT.PREVIEW.TEAMS.SAVE')"
        size="sm"
        :is-loading="isSaving"
        @click="saveTeams"
      />
    </div>
  </section>
</template>
